import dayjs, { Dayjs } from 'dayjs'
import {
  customParseFloat,
  customParseInt,
  parseFixedFloat,
  truncateDecimals
} from '@/core/utils/parsing.ts'
import { Quote } from '@/modules/sales/clients/types/Quote.ts'

type ClientCredit = {
  quotes: { quote: string | number; status: Quote['status'] }[]
}

export const sumUnpaidQuotes = (
  quotes: { quote: string | number; status: Quote['status'] }[]
) => {
  return quotes.reduce((total, quote) => {
    return quote.status !== 'PAGADA' ? total + Number(quote.quote) : total
  }, 0)
}

export const getClientDebit = (clientCredits: ClientCredit[]) => {
  return clientCredits.reduce((debt, credit) => {
    return credit.quotes ? debt + sumUnpaidQuotes(credit.quotes) : debt
  }, 0)
}

export type FinancialInfo = {
  initial: number | string
  additional_cost: number | string
  initial_total: number | string
  payment_cuotes: number | string
  total_payment: number | string
  tasa: number | string
  rentability: number | string
  list_cuotes: { date: string; quote: string | number }[]
  payment_extra: number | string
  policyPercentage?: number | string | null
  policyValueToSum?: number | string | null
}

interface Props {
  initialByLevel?: number
  initial?: number
  clientCreditValue: number | string
  offerDiscountPercent: number | string | null
  isOfferWithoutCreditLimit: boolean
  rentabilityTasa: number | string
  rentabilityCantCuotes: number | string
  rentabilityTimesCuotes: number | string
  cost: number | string
  payment_extra: number | string
  policyPercentage?: number | string | null
  maxCost?: number | string | null
  date: Dayjs
}

export const calculateFinancialInfo = ({
  initialByLevel,
  initial,
  clientCreditValue,
  offerDiscountPercent,
  isOfferWithoutCreditLimit,
  rentabilityTasa,
  rentabilityCantCuotes,
  rentabilityTimesCuotes,
  cost,
  payment_extra,
  policyPercentage,
  maxCost,
  date
}: Props) => {
  clientCreditValue = customParseFloat(clientCreditValue)
  // NaN is the internal "no offer" sentinel: a real 0 means a 0% promotion.
  offerDiscountPercent =
    offerDiscountPercent != null ? customParseFloat(offerDiscountPercent) : NaN
  rentabilityTasa = customParseFloat(rentabilityTasa)
  rentabilityCantCuotes = customParseFloat(rentabilityCantCuotes)
  rentabilityTimesCuotes = customParseInt(rentabilityTimesCuotes)
  cost = customParseFloat(cost)
  payment_extra = customParseFloat(payment_extra)
  maxCost =
    maxCost !== null && maxCost !== undefined ? customParseFloat(maxCost) : 0
  policyPercentage =
    policyPercentage !== null && policyPercentage !== undefined
      ? customParseFloat(policyPercentage)
      : null

  let initialPercent = (initialByLevel || 0) / 100

  if (!Number.isNaN(offerDiscountPercent)) {
    initialPercent = offerDiscountPercent / 100
  }

  const f: FinancialInfo = {
    initial: 0,
    additional_cost: 0,
    initial_total: 0,
    payment_cuotes: 0,
    total_payment: 0,
    tasa: 0,
    rentability: 0,
    list_cuotes: [],
    payment_extra: 0,
    policyPercentage: null,
    policyValueToSum: null
  }

  const maxCostExcess = maxCost && cost > maxCost ? cost - maxCost : 0

  if (policyPercentage) {
    const policyValueToSum = Math.ceil(cost * (policyPercentage / 100))
    const policyValuePerQuote = Math.ceil(
      policyValueToSum / rentabilityCantCuotes
    )
    f.policyPercentage = policyPercentage
    f.policyValueToSum = policyValuePerQuote * rentabilityCantCuotes
  } else {
    f.policyPercentage = 0
    f.policyValueToSum = 0
  }

  if (cost > clientCreditValue) {
    f.additional_cost = cost - clientCreditValue
  }

  if (customParseFloat(f.additional_cost) < 0 || isOfferWithoutCreditLimit) {
    f.additional_cost = 0
  }

  f.additional_cost = customParseFloat(f.additional_cost) + maxCostExcess

  f.initial =
    initial !== undefined
      ? initial
      : (cost - customParseFloat(f.additional_cost)) * initialPercent
  f.initial = Math.ceil(f.initial)

  f.initial_total =
    customParseFloat(f.initial) + customParseFloat(f.additional_cost)

  f.payment_cuotes = cost - f.initial_total

  f.initial_total = f.initial_total + payment_extra
  f.payment_cuotes = f.payment_cuotes - payment_extra
  f.payment_extra = payment_extra
  f.payment_extra = parseFixedFloat(f.payment_extra)

  f.tasa = f.payment_cuotes * rentabilityTasa
  f.tasa = Math.ceil(f.tasa)

  f.initial = f.initial.toFixed(2)
  f.initial_total = f.initial_total.toFixed(2)
  f.payment_cuotes = f.payment_cuotes.toFixed(2)
  f.tasa = f.tasa.toFixed(2)
  f.additional_cost = parseFixedFloat(f.additional_cost)

  let quote: number | string = Math.ceil(
    customParseFloat(f.tasa) / rentabilityCantCuotes
  )
  quote = parseFixedFloat(
    quote + Number(f.policyValueToSum) / rentabilityCantCuotes
  )

  if (date) {
    for (let i = 1; i <= rentabilityCantCuotes; i++) {
      const days = i * rentabilityTimesCuotes
      f.list_cuotes.push({
        date: dayjs(date).add(days, 'days').format('DD/MM/YYYY'),
        quote: quote
      })
    }
  }

  f.total_payment = customParseFloat(quote) * rentabilityCantCuotes
  f.total_payment = f.total_payment.toFixed(2)

  f.rentability =
    customParseFloat(f.total_payment) - customParseFloat(f.payment_cuotes)

  f.initial = truncateDecimals(f.initial)
  f.additional_cost = truncateDecimals(f.additional_cost)
  f.initial_total = truncateDecimals(f.initial_total)
  f.payment_cuotes = truncateDecimals(f.payment_cuotes)
  f.total_payment = truncateDecimals(f.total_payment)
  f.tasa = truncateDecimals(f.tasa)
  f.rentability = truncateDecimals(f.rentability)
  f.payment_extra = truncateDecimals(f.payment_extra)

  return f
}
